import { useLazyQuery, useMutation } from '@apollo/client';
import { userSelector } from 'common/globalComponents/selectors';
import Loader from 'components/Loader';
import SlugInput from 'components/SlugInput';
import AssetImageUploader from 'containers/blog/BlogList/components/AssetImageUploader';
import { useFormik } from 'formik';
import useRequest from 'hooks/useRequest';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap';
import { createStructuredSelector } from 'reselect';
import {
  FIRST_FOOD_DIETARY_TAGS,
  FIRST_FOOD_RELIGIOUS_AND_DIETARY,
  FOOD_ITEM_GROUP,
  FOOD_ITEM_ORIGINS,
  FOOD_ITEM_REF_SOURCES,
  LANGUAGE_ARRAY_WITH_STRING,
  MODULE_TYPE,
  taxonomyType,
} from 'utils/constants';
import { titleCaseIfExists } from 'utils/helper';
import * as Yup from 'yup';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { SMKLinks } from '../../../utils/links';
import MultipleSearch from '../components/multipleSearch';
import NutritionCard from '../components/nutritionCard';
import { ADD_FOOD_ITEMS, UPDATE_FOOD_ITEMS } from '../graphql/food_item.mutation';
import { FOOD_ITEMS_GET_BY_ID } from '../graphql/food_item.query';

const stateSelector = createStructuredSelector({
  user: userSelector,
});

const AddUpdateFoodItems = () => {
  const history = useHistory();
  const { id: foodItemID } = useParams();

  const { user } = useSelector(stateSelector);

  toast.configure();

  const refSourcesList = Object.keys(FOOD_ITEM_REF_SOURCES)?.map((e) => ({
    label: e?.toUpperCase(),
    value: FOOD_ITEM_REF_SOURCES[e],
  }));

  const [foodItemTypesList, setFoodItemTypesList] = useState(null);

  const [getFoodItemTypesList, { loading: getFoodItemTypesLoading }] = useRequest({
    url: '/common/admin/taxonomy/search',
    method: 'POST',
    onSuccess: (data) => {
      setFoodItemTypesList(
        data?.results?.map((e) => ({
          label: titleCaseIfExists(e?.name?.replaceAll('-', ' ')),
          value: e?.id,
        }))
      );
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [nutritionValues, setNutritionValues] = useState({});
  const [foodItemDetails, setFoodItemDetails] = useState();

  const [getFoofDetailsRequest, { loading: getFoofDetailsLoading }] = useLazyQuery(FOOD_ITEMS_GET_BY_ID, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_nutrition_foodItem_getById)) {
        setFoodItemDetails(data?.admin_nutrition_foodItem_getById);
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const [addFoodItemRequest, { loading: addFoodItemLoading }] = useMutation(ADD_FOOD_ITEMS, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_nutrition_foodItem_add)) {
        toast.success(`Success! Food Item has been added.`);
        history.replace(SMKLinks.FOOD_ITEMS_LIST);
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const [updateFoodItemRequest, { loading: updateFoodItemLoading }] = useMutation(UPDATE_FOOD_ITEMS, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_nutrition_foodItem_update)) {
        toast.success(`Success! Food Item has been updated.`);
        window.location.reload();
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const editInitialValues = () => {
    setNutritionValues(foodItemDetails?.nutrition);
    return {
      id: foodItemDetails?.id,
      active: foodItemDetails?.active,
      deleted: foodItemDetails?.deleted,
      isAllergen: foodItemDetails?.isAllergen,
      name: foodItemDetails?.name,
      slug: foodItemDetails?.slug,
      description: foodItemDetails?.description,
      order: foodItemDetails?.order,
      refSource: foodItemDetails?.refSource,
      refId: foodItemDetails?.refId,
      image: foodItemDetails?.image || {},
      language: foodItemDetails?.language,
      substitutes: foodItemDetails?.substitutes,
      nutrition: foodItemDetails?.nutrition,
      typeId: foodItemDetails?.typeId,

      foodGroup: foodItemDetails?.foodGroup,
      foodOrigin: foodItemDetails?.foodOrigin,
      dietaryTags: foodItemDetails?.dietaryTags,
      religiousAndDietary: {
        isVegetarian: foodItemDetails?.religiousAndDietary?.isVegetarian,
        isHalal: foodItemDetails?.religiousAndDietary?.isHalal,
        isKosher: foodItemDetails?.religiousAndDietary?.isKosher,
      },
    };
  };

  const getInitialValues = () => ({
    active: true,
    deleted: false,
    isAllergen: false,
    order: 0,
    name: '',
    slug: '',
    description: '',
    refId: '',
    refSource: FOOD_ITEM_REF_SOURCES.IFCT,
    language: LANGUAGE_ARRAY_WITH_STRING[0]?.value,
    image: {},
    substitutes: [],
    nutrition: {},
    typeId: null,

    foodGroup: '',
    foodOrigin: '',
    dietaryTags: [],
    religiousAndDietary: {
      isVegetarian: false,
      isHalal: false,
      isKosher: false,
    },
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: getInitialValues(),
    onSubmit: (values) => {
      const data = {
        active: values?.active,
        deleted: values?.deleted,
        isAllergen: values?.isAllergen,
        order: values?.order,
        name: values?.name,
        slug: values?.slug,
        description: values?.description,

        foodGroup: values?.foodGroup,
        foodOrigin: values?.foodOrigin,
        dietaryTags: values?.dietaryTags,
        religiousAndDietary: {
          isVegetarian: values?.religiousAndDietary?.isVegetarian,
          isHalal: values?.religiousAndDietary?.isHalal,
          isKosher: values?.religiousAndDietary?.isKosher,
        },

        refId: values?.refId,
        refSource: values?.refSource,
        language: values?.language,
        ...(!isEmpty(values?.image)
          ? {
              imageId: values?.image?.id,
            }
          : { imageId: !isEmpty(foodItemDetails) ? -1 : null }),

        image: undefined,
        substitutes: values?.substitutes,
        nutrition: {
          ...values?.nutrition,
          calories: !isEmpty(String(values?.nutrition?.calories)) ? values?.nutrition?.calories : undefined,
          glycemicIndex: !isEmpty(String(values?.nutrition?.glycemicIndex))
            ? values?.nutrition?.glycemicIndex
            : undefined,
        },

        typeId: values?.typeId,
      };

      isEmpty(foodItemID)
        ? addFoodItemRequest({
            variables: {
              foodItemDTO: {
                ...data,
              },
            },
          })
        : updateFoodItemRequest({
            variables: {
              id: foodItemID,
              foodItemDTO: {
                ...data,
              },
            },
          });
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required('Please provide name'),
    }),
  });

  useEffect(() => {
    if (!isEmpty(foodItemDetails)) {
      formik.setValues(editInitialValues());
    }
  }, [foodItemDetails]);

  useEffect(() => {
    if (!isEmpty(foodItemID)) {
      getFoofDetailsRequest({
        variables: {
          id: foodItemID,
        },
      });
    }
  }, [foodItemID]);

  useEffect(() => {
    getFoodItemTypesList({
      body: {
        page: 1,
        size: 500,
        sortBy: 'id',
        sortOrder: 0,
        type: taxonomyType.NUTRITION_FOOD_ITEM_TYPE,
      },
    });
  }, []);

  return (
    <div className="page-content">
      <Loader
        isActive={addFoodItemLoading || getFoofDetailsLoading || updateFoodItemLoading || getFoodItemTypesLoading}
      />
      <Container fluid>
        <Breadcrumbs title="Food Items" breadcrumbItem={`${isEmpty(foodItemID) ? 'Add' : 'Edit'}  Food Items`} />

        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={9}>
              <Card>
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="name" className="form-label  ">
                          Name
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          className="form-control"
                          placeholder="Name.."
                          invalid={!!(formik.touched.name && formik.errors.name)}
                          {...formik.getFieldProps('name')}
                        />
                        <FormFeedback>{formik.errors.name}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col md={12} className="mb-4">
                      <SlugInput
                        formik={formik}
                        ID={foodItemID}
                        nameValue={formik.values.name}
                        formGroupClassName={'mt-0'}
                        showResetRegenerate={true}
                        currentSlug={foodItemDetails?.slug}
                      />
                    </Col>

                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="description" className="form-label  ">
                          Description
                        </Label>
                        <Input
                          id="description"
                          name="description"
                          style={{ height: '100px' }}
                          type="textarea"
                          className="form-control"
                          placeholder="Description.."
                          invalid={!!(formik.touched.description && formik.errors.description)}
                          {...formik.getFieldProps('description')}
                        />
                        <FormFeedback>{formik.errors.description}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="typeId" className="form-label  ">
                          Food Item Type
                        </Label>
                        <Select
                          options={foodItemTypesList}
                          placeholder="Select Food Item Type..."
                          value={foodItemTypesList?.find((e) => e?.value === formik.values.typeId)}
                          onChange={(e) => formik.setFieldValue('typeId', e?.value)}
                        />
                        <FormFeedback>{formik.errors.typeId}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="order" className="form-label  ">
                          Order
                        </Label>
                        <Input
                          id="order"
                          name="order"
                          type="number"
                          className="form-control"
                          placeholder="Order.."
                          invalid={!!(formik.touched.order && formik.errors.order)}
                          {...formik.getFieldProps('order')}
                        />
                        <FormFeedback>{formik.errors.order}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <FormGroup className="mb-4">
                        <Label for="refSource" className="form-label  ">
                          Ref Source
                        </Label>
                        <Select
                          options={refSourcesList}
                          placeholder="Select source..."
                          value={refSourcesList?.find(
                            (e) => e?.value?.toLowerCase() === formik.values.refSource?.toLowerCase()
                          )}
                          onChange={(e) => formik.setFieldValue('refSource', e?.value)}
                        />
                        <FormFeedback>{formik.errors.refSource}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={9}>
                      <FormGroup className="mb-4">
                        <Label for="refId" className="form-label  ">
                          Ref ID
                        </Label>
                        <Input
                          id="refId"
                          name="refId"
                          type="text"
                          className="form-control"
                          placeholder="Ref ID.."
                          invalid={!!(formik.touched.refId && formik.errors.refId)}
                          {...formik.getFieldProps('refId')}
                        />
                        <FormFeedback>{formik.errors.refId}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="foodGroup" className="form-label  ">
                          Food Group
                        </Label>
                        <Select
                          options={FOOD_ITEM_GROUP}
                          placeholder="Select Food group..."
                          value={FOOD_ITEM_GROUP?.find((e) => e?.value === formik.values.foodGroup)}
                          onChange={(e) => formik.setFieldValue('foodGroup', e?.value)}
                        />
                        <FormFeedback>{formik.errors.foodGroup}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="foodOrigin" className="form-label  ">
                          Food Origin
                        </Label>
                        <Select
                          options={FOOD_ITEM_ORIGINS}
                          placeholder="Select Food origin..."
                          value={FOOD_ITEM_ORIGINS?.find((e) => e?.value === formik.values.foodOrigin)}
                          onChange={(e) => formik.setFieldValue('foodOrigin', e?.value)}
                        />
                        <FormFeedback>{formik.errors.foodOrigin}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col md={12}>
                      <Row>
                        <Col className="mb-4" md={6}>
                          <Card className="bg-light">
                            <CardBody>
                              <h5 className="border bg-white p-2 mb-3 text-center w-100 rounded-2">Dietary tags</h5>
                              <ul className="ps-0 row">
                                {FIRST_FOOD_DIETARY_TAGS?.map((item, index) => {
                                  const isChecked = formik.values?.dietaryTags?.includes(item);

                                  return (
                                    <li className="mb-1 col-lg-6" style={{ listStyle: 'none' }} key={`item_${index}`}>
                                      <label>
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={() => {
                                            const values = formik.values.dietaryTags || [];
                                            if (isChecked) {
                                              formik.setFieldValue(
                                                'dietaryTags',
                                                values.filter((e) => e !== item)
                                              );
                                            } else {
                                              formik.setFieldValue('dietaryTags', [...values, item]);
                                            }
                                          }}
                                        />
                                        <span className="ps-1">
                                          {titleCaseIfExists(item?.replaceAll('_', ' ')?.replaceAll('-', ' '))}
                                        </span>
                                      </label>
                                    </li>
                                  );
                                })}
                              </ul>
                            </CardBody>
                          </Card>
                        </Col>
                        <Col className="mb-4" md={6}>
                          <Card className="bg-light">
                            <CardBody>
                              <h5 className="border bg-white p-2 mb-3 text-center w-100 rounded-2">
                                Religious & Dietary
                              </h5>
                              <ul className="ps-0 row">
                                {FIRST_FOOD_RELIGIOUS_AND_DIETARY?.map((item, index) => {
                                  const valuesObj = formik.values.religiousAndDietary || {};
                                  const isChecked = !!valuesObj[item?.value];

                                  return (
                                    <li
                                      className="mb-1 col-lg-6"
                                      style={{ listStyle: 'none' }}
                                      key={`dietary_${index}`}>
                                      <label>
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={() => {
                                            formik.setFieldValue('religiousAndDietary', {
                                              ...valuesObj,
                                              [item?.value]: !isChecked,
                                            });
                                          }}
                                        />
                                        <span className="ps-1">{item?.label}</span>
                                      </label>
                                    </li>
                                  );
                                })}
                              </ul>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <MultipleSearch formik={formik} label={'substitutes'} />

              <NutritionCard formik={formik} formikLabel={'nutrition'} values={nutritionValues} />

              <Col md={12}>
                <Card>
                  <CardBody>
                    <Table className="project-list-table  table-centered table-border">
                      <thead>
                        <tr className="table-warning">
                          <th scope="col">Category</th>
                          <th scope="col">FoodGroup</th>
                          <th scope="col">FoodOrigin</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>others</td>
                          <td>MISC</td>
                          <td>MIXED or UNKNOWN</td>
                        </tr>
                        <tr>
                          <td>legumes-and-legume-products</td>
                          <td>LEGUME</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>fruits</td>
                          <td>FRUIT</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>vegetables-and-vegetable-products</td>
                          <td>VEGETABLE</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>sausages-and-luncheon-meats</td>
                          <td>PROTEIN</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>spices-and-herbs</td>
                          <td>HERB_SPICE</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>nut-and-seed-products</td>
                          <td>NUT</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>egg-products</td>
                          <td>PROTEIN</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>dairy-and-egg-products</td>
                          <td>DAIRY</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>fruit-juices</td>
                          <td>FRUIT</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>fruits-and-fruit-juices</td>
                          <td>FRUIT</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>baked-products</td>
                          <td>SWEET or GRAIN</td>
                          <td>MIXED</td>
                        </tr>
                        <tr>
                          <td>fats-and-oils</td>
                          <td>FAT_OIL</td>
                          <td>PLANT_BASED / ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>poultry-products</td>
                          <td>PROTEIN</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>soups-sauces-and-gravies</td>
                          <td>MISC</td>
                          <td>MIXED</td>
                        </tr>
                        <tr>
                          <td>sweets</td>
                          <td>SWEET</td>
                          <td>MIXED or PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>finfish-and-shellfish-products</td>
                          <td>SEAFOOD</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>restaurant-foods</td>
                          <td>MISC</td>
                          <td>MIXED</td>
                        </tr>
                        <tr>
                          <td>beef-products</td>
                          <td>PROTEIN</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>pork-products</td>
                          <td>PROTEIN</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>cereal-grains-and-pasta</td>
                          <td>GRAIN</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>beverages</td>
                          <td>BEVERAGE</td>
                          <td>MIXED</td>
                        </tr>
                        <tr>
                          <td>cereals-and-millets</td>
                          <td>GRAIN</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>grain-legumes</td>
                          <td>LEGUME</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>green-leafy-vegetables</td>
                          <td>VEGETABLE</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>other-vegetables</td>
                          <td>VEGETABLE</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>roots-and-tubers</td>
                          <td>VEGETABLE</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>condiments-and-spices</td>
                          <td>HERB_SPICE</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>nuts-and-oil-seeds</td>
                          <td>NUT</td>
                          <td>PLANT_BASED</td>
                        </tr>
                        <tr>
                          <td>sugars</td>
                          <td>SWEET</td>
                          <td>PLANT_BASED or SYNTHETIC</td>
                        </tr>
                        <tr>
                          <td>mushrooms</td>
                          <td>FUNGI</td>
                          <td>FUNGAL</td>
                        </tr>
                        <tr>
                          <td>miscellaneous-foods</td>
                          <td>MISC</td>
                          <td>MIXED</td>
                        </tr>
                        <tr>
                          <td>milk-and-milk-products</td>
                          <td>DAIRY</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>egg-and-egg-products</td>
                          <td>PROTEIN</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>poultry</td>
                          <td>PROTEIN</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>animal-meat</td>
                          <td>PROTEIN</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>marine-fish</td>
                          <td>SEAFOOD</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>marine-shellfish</td>
                          <td>SEAFOOD</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>marine-mollusks</td>
                          <td>SEAFOOD</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>fresh-water-fish-and-shellfish</td>
                          <td>SEAFOOD</td>
                          <td>ANIMAL_BASED</td>
                        </tr>
                        <tr>
                          <td>edible-oils-and-fats</td>
                          <td>FAT_OIL</td>
                          <td>PLANT_BASED / ANIMAL_BASED</td>
                        </tr>
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </Col>
            </Col>
            <Col md={3}>
              <Card>
                <CardBody>
                  {user?.entityPermissions?.nutrition_recipe?.publish && foodItemID && (
                    <Button
                      type="button"
                      color={foodItemDetails?.active ? 'danger' : 'success'}
                      className="mb-3 w-100"
                      onClick={() => {
                        const action = foodItemDetails?.active ? 'unpublish' : 'publish';
                        const isConfirm = confirm(`Are you sure you want to ${action} this item?`);

                        if (isConfirm) {
                          formik.setFieldValue('active', !foodItemDetails?.active);
                          formik.setFieldValue('deleted', false);
                          formik.handleSubmit();
                        }
                      }}>
                      {foodItemDetails?.active ? 'Unpublish' : 'Publish'}
                    </Button>
                  )}

                  <Button type="submit" color="primary" className="mb-3 w-100">
                    {foodItemID ? 'Save' : 'Add'}
                  </Button>

                  <Button
                    className="w-100"
                    color="light"
                    onClick={() => {
                      history.replace(SMKLinks.FOOD_ITEMS_LIST);
                    }}>
                    Cancel
                  </Button>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  {/* {['active', 'deleted', 'isAllergen']?.map((elem, index) => ( */}
                  {['isAllergen']?.map((elem, index) => (
                    <FormGroup className={`mb-3`} key={index}>
                      <Label className="mb-3">{elem === 'isAllergen' ? 'isAllergen' : titleCaseIfExists(elem)}</Label>
                      <div className="d-flex align-items-center">
                        {[true, false].map((option, i) => (
                          <div key={`${elem}_${i}`} className="form-check d-inline-block me-4">
                            <input
                              type="radio"
                              id={`${elem}_${option}`}
                              name={elem}
                              className="form-check-input"
                              checked={formik.values[elem] === option}
                              onChange={() => formik.setFieldValue(`${elem}`, option)}
                            />
                            <label className="form-check-label" htmlFor={`${elem}_${option}`}>
                              {option ? 'Yes' : 'No'}
                            </label>
                          </div>
                        ))}
                      </div>
                    </FormGroup>
                  ))}
                </CardBody>
              </Card>

              <Card className="border">
                <CardBody>
                  <Row>
                    <Col md={12}>
                      <CardTitle className="mb-3">Language </CardTitle>
                      {LANGUAGE_ARRAY_WITH_STRING?.map((option, i) => (
                        <div key={`language_${i}`} className="form-check mb-3 d-inline-block me-4">
                          <input
                            type="radio"
                            id={`language_${option?.value}`}
                            name="language"
                            className="form-check-input"
                            checked={formik.values.language === option?.value}
                            onChange={() => formik.setFieldValue('language', option?.value)}
                          />
                          <label className="form-check-label" htmlFor={`language_${option?.value}`}>
                            {option?.label}
                          </label>
                        </div>
                      ))}
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <AssetImageUploader
                forType={MODULE_TYPE.RECIPE}
                formik={formik}
                fieldName={'image'}
                label={'Select Image'}
              />
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default AddUpdateFoodItems;
